import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CalendarClock, Plus, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function startOfWeek(d) { const wd = (d.getDay() + 6) % 7; const s = new Date(d); s.setDate(d.getDate() - wd); s.setHours(0, 0, 0, 0); return s; }

export default function ShiftsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("week"); // day | week | month
  const [anchor, setAnchor] = useState(() => new Date());
  const [rows, setRows] = useState([]);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [fOffice, setFOffice] = useState("");
  const [fOperator, setFOperator] = useState("");
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [edit, setEdit] = useState(null);

  const range = useMemo(() => {
    if (view === "day") return [iso(anchor), iso(anchor)];
    if (view === "week") { const s = startOfWeek(anchor); const e = new Date(s); e.setDate(s.getDate() + 6); return [iso(s), iso(e)]; }
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1); const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return [iso(s), iso(e)];
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: range[0], date_to: range[1] };
      if (fOffice) params.office_id = fOffice;
      if (fOperator) params.operator_id = fOperator;
      const { data } = await api.get("/shifts", { params });
      setRows(data);
    } finally { setLoading(false); }
  }, [range, fOffice, fOperator]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [isAdmin]);

  const del = async (id) => { try { await api.delete(`/shifts/${id}`); toast.success("Turno eliminato"); load(); } catch { toast.error("Errore"); } };
  const move = (dir) => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(d);
  };

  const byDate = useMemo(() => {
    const m = {};
    rows.forEach((s) => { (m[s.shift_date] = m[s.shift_date] || []).push(s); });
    return m;
  }, [rows]);

  const weekDays = useMemo(() => {
    const s = startOfWeek(anchor); return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; });
  }, [anchor]);

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="shifts-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="ds-page-title flex items-center gap-2"><CalendarClock className="h-6 w-6 text-brand" /> Turni</h1>
          <p className="mt-1 text-sm text-slate-500">{isAdmin ? "Organizzazione presenza operatori in sede." : "I tuoi turni."}</p>
        </div>
        {isAdmin && <Button onClick={() => { setEdit(null); setDlg(true); }} className="gap-1.5" data-testid="new-shift-btn"><Plus className="h-4 w-4" /> Nuovo turno</Button>}
      </div>

      <div className="ds-card flex flex-wrap items-center gap-3 p-4" data-testid="shifts-toolbar">
        <div className="flex gap-1">
          {["day", "week", "month"].map((v) => <Button key={v} size="sm" variant={view === v ? "default" : "outline"} onClick={() => setView(v)} data-testid={`shift-view-${v}`}>{v === "day" ? "Giorno" : v === "week" ? "Settimana" : "Mese"}</Button>)}
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={() => move(-1)} data-testid="shift-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[150px] text-center text-sm font-medium text-slate-700">{range[0]} → {range[1]}</span>
          <Button size="icon" variant="ghost" onClick={() => move(1)} data-testid="shift-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        {isAdmin && (<>
          <Select value={fOffice || "all"} onValueChange={(v) => setFOffice(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px]" data-testid="shift-office-filter"><SelectValue placeholder="Sede" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutte</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fOperator || "all"} onValueChange={(v) => setFOperator(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="shift-operator-filter"><SelectValue placeholder="Operatore" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </>)}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        : view === "week" ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-7" data-testid="shifts-week">
            {weekDays.map((d, i) => {
              const key = iso(d); const list = byDate[key] || [];
              return (
                <div key={key} className="ds-card min-h-[120px] p-3">
                  <div className="mb-2 text-xs font-semibold text-slate-500">{DAYS[i]} {d.getDate()}/{d.getMonth() + 1}</div>
                  <div className="space-y-1.5">
                    {list.length === 0 ? <div className="text-xs text-slate-300">—</div>
                      : list.map((s) => <ShiftChip key={s.id} s={s} isAdmin={isAdmin} onEdit={() => { setEdit(s); setDlg(true); }} onDel={() => del(s.id)} />)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="ds-card ds-table-scroll p-0" data-testid="shifts-list">
            {rows.length === 0 ? <div className="py-10 text-center text-sm text-slate-500" data-testid="shifts-empty">Nessun turno nel periodo.</div>
              : (
                <table className="w-full text-sm">
                  <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Operatore</th><th className="px-4 py-3">Sede</th><th className="px-4 py-3">Orario</th><th className="px-4 py-3">Note</th><th className="px-4 py-3"></th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((s) => (
                      <tr key={s.id} className="hover:bg-brand/5" data-testid={`shift-row-${s.id}`}>
                        <td className="px-4 py-3">{s.shift_date}</td><td className="px-4 py-3 font-medium text-slate-800">{s.operator_name}</td>
                        <td className="px-4 py-3">{s.office_name || "—"}</td><td className="px-4 py-3">{s.start_time}–{s.end_time}</td><td className="px-4 py-3 text-xs text-slate-500">{s.notes || "—"}</td>
                        <td className="px-4 py-3">{isAdmin && <div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEdit(s); setDlg(true); }} data-testid={`shift-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(s.id)} data-testid={`shift-del-${s.id}`}><Trash2 className="h-4 w-4 text-danger" /></Button></div>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        )}
      {dlg && <ShiftDialog open={dlg} onOpenChange={setDlg} shift={edit} offices={offices} operators={operators} onSaved={load} />}
    </div>
  );
}

function ShiftChip({ s, isAdmin, onEdit, onDel }) {
  return (
    <div className="rounded-md border border-brand/20 bg-brand/5 px-2 py-1 text-xs" data-testid={`shift-chip-${s.id}`}>
      <div className="font-semibold text-brand">{s.start_time}–{s.end_time}</div>
      <div className="truncate text-slate-600">{s.operator_name}</div>
      {isAdmin && <div className="mt-1 flex gap-1"><button onClick={onEdit} className="text-slate-400 hover:text-brand" data-testid={`shift-chip-edit-${s.id}`}><Pencil className="h-3 w-3" /></button><button onClick={onDel} className="text-slate-400 hover:text-danger" data-testid={`shift-chip-del-${s.id}`}><Trash2 className="h-3 w-3" /></button></div>}
    </div>
  );
}

function ShiftDialog({ open, onOpenChange, shift, offices, operators, onSaved }) {
  const editing = !!shift;
  const [f, setF] = useState({
    operator_id: shift?.operator_id || "", office_id: shift?.office_id || "",
    shift_date: shift?.shift_date || "", start_time: shift?.start_time || "09:00",
    end_time: shift?.end_time || "13:00", notes: shift?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [warn, setWarn] = useState([]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setError(""); setWarn([]); setSaving(true);
    try {
      const payload = { ...f, office_id: f.office_id || null, notes: f.notes || null };
      const { data } = editing ? await api.put(`/shifts/${shift.id}`, payload) : await api.post("/shifts", payload);
      if (data.overlap_warning?.length) { setWarn(data.overlap_warning); toast.warning("Turno salvato con sovrapposizione segnalata"); }
      else toast.success(editing ? "Turno aggiornato" : "Turno creato");
      onSaved();
      if (!data.overlap_warning?.length) onOpenChange(false);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="shift-dialog">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Modifica turno" : "Nuovo turno"}</DialogTitle>
          <DialogDescription>Operatore, sede, data e orario.</DialogDescription></DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="shift-error">{error}</div>}
        {warn.length > 0 && <div className="rounded-lg border border-warning/25 bg-warning/10 px-3 py-2 text-sm text-warning" data-testid="shift-warn">Attenzione: sovrapposizione con {warn.length} turno/i dello stesso operatore in questa data.</div>}
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Operatore *</Label>
            <Select value={f.operator_id} onValueChange={(v) => set("operator_id", v)}>
              <SelectTrigger data-testid="shift-operator"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent className="bg-white">{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Sede</Label>
            <Select value={f.office_id || "none"} onValueChange={(v) => set("office_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="shift-office"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Data *</Label><Input type="date" value={f.shift_date} onChange={(e) => set("shift_date", e.target.value)} data-testid="shift-date" /></div>
            <div className="space-y-1.5"><Label>Inizio *</Label><Input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="shift-start" /></div>
            <div className="space-y-1.5"><Label>Fine *</Label><Input type="time" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} data-testid="shift-end" /></div>
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="shift-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button><Button onClick={save} disabled={saving} data-testid="shift-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
